import { useMemo, useState, useEffect } from 'react';
import useSWR from 'swr';
import {
 Stack,
 Button,
 Heading,
 HStack,
 Center,
 Container,
 useToast,
 Flex,
 Text,
 Box,
} from '@chakra-ui/react';
import { InputText, Select, YearPickerInput } from '../../components/Input';
import Loader from '../../components/Feedback/Loader';
import { QuestionnaireHeaderNew } from '../../components/Form';
import { fetchData } from '../../services/fetch';
import { useForm } from 'react-hook-form';
import baseService from '../../services/base';
import { SuccessModal } from '../../components/Modals';
import { useNavigate, useParams } from 'react-router-dom';
import ButtonOnlyIcon from '../../components/Button/Only-icon';
import SealAssetInput from '../../components/Certification/SealAssetInput';
import MonthRangeFields from '../../components/Certification/MonthRangeFields';
import { SEAL_MODELS } from '../../resources/sealModels';
import { monthToStartDate, monthToEndDate, dateToMonth } from '../../utils/monthRange';

export default function EditCertification() {
 const toast = useToast();
 const navigate = useNavigate();
 const { id: certificationId } = useParams();

 const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
 const [chosenQuestionnaire, setChosenQuestionnaire] = useState(null);
 const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
 const [certificationName, setCertificationName] = useState('');
 const [certificationExercise, setCertificationExercise] = useState(null);
 const [initialCertificationName, setInitialCertificationName] = useState('');
 const [initialCertificationExercise, setInitialCertificationExercise] = useState(null);
 const [initialChosenQuestionnaire, setInitialChosenQuestionnaire] = useState(null);
 const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
 const [isQuestionnaireCheckComplete, setIsQuestionnaireCheckComplete] = useState(false);
 const [cutoffScores, setCutoffScores] = useState({});
 const [initialCutoffScores, setInitialCutoffScores] = useState({});
 // EG-27 (rework): janela de validade mês/ano (YYYY-MM) POR SELO.
 const [validFromMonth, setValidFromMonth] = useState('');
 const [initialValidFromMonth, setInitialValidFromMonth] = useState('');
 const [validUntilMonth, setValidUntilMonth] = useState('');
 const [initialValidUntilMonth, setInitialValidUntilMonth] = useState('');
 const [validFromMonth2, setValidFromMonth2] = useState('');
 const [initialValidFromMonth2, setInitialValidFromMonth2] = useState('');
 const [validUntilMonth2, setValidUntilMonth2] = useState('');
 const [initialValidUntilMonth2, setInitialValidUntilMonth2] = useState('');
 // EG-27 (rework): tipo por selo + toggle do 2º selo.
 const [sealModel, setSealModel] = useState('ESG');
 const [initialSealModel, setInitialSealModel] = useState('ESG');
 const [sealModel2, setSealModel2] = useState('ESG');
 const [initialSealModel2, setInitialSealModel2] = useState('ESG');
 const [seal2Enabled, setSeal2Enabled] = useState(false);
 const [initialSeal2Enabled, setInitialSeal2Enabled] = useState(false);

 const {
  data: certificateData,
  isLoading,
  mutate,
 } = useSWR(certificationId ? `/certificate/${certificationId}` : null, fetchData);

 const { data: certificateAssets, mutate: mutateAssets } = useSWR(
  certificationId ? `/certificate/${certificationId}/assets` : null,
  fetchData
 );

 const { data: defaultAssets } = useSWR('/certificate/assets/default', fetchData);
 // EG-27: arte por modelo (p/ a prévia do selo refletir o tipo escolhido).
 const { data: sealModelsData } = useSWR('/certificate/seal-models', fetchData);
 const modelUrl = (m) =>
  (Array.isArray(sealModelsData) ? sealModelsData.find((x) => x.model === m) : null)
   ?.url || null;

 // EG-23/EG-24: selo e guia salvam na hora (independente do submit do form).
 const saveAsset = async (payload, okMsg) => {
  try {
   await baseService.patch(`certificate/${certificationId}/assets`, payload);
   await mutateAssets();
   toast({ title: okMsg, status: 'success' });
  } catch (error) {
   toast({
    title: 'Erro ao salvar',
    description: error.response?.data?.message || 'Tente novamente mais tarde',
    status: 'error',
   });
   throw error;
  }
 };
 const handleSealUpload = (asset) =>
  saveAsset({ seal: asset }, 'Selo atualizado com sucesso');
 const handleSeal2Upload = (asset) =>
  saveAsset({ seal2: asset }, 'Variação do selo atualizada com sucesso');
 const handleGuideUpload = (asset) =>
  saveAsset({ guide: asset }, 'Guia atualizada com sucesso');

 const resetAsset = async (type, okMsg) => {
  try {
   await baseService.delete(`certificate/${certificationId}/assets/${type}`);
   await mutateAssets();
   toast({ title: okMsg, status: 'success' });
  } catch (error) {
   toast({
    title: 'Erro ao resetar',
    description: error.response?.data?.message || 'Tente novamente mais tarde',
    status: 'error',
   });
   throw error;
  }
 };
 const handleSealReset = () => resetAsset('seal', 'Selo resetado para o padrão');
 const handleSeal2Reset = () =>
  resetAsset('seal2', 'Variação do selo removida');
 const handleGuideReset = () => resetAsset('guide', 'Guia resetada para o padrão');

 const { data: questionnaires } = useSWR('/survey/certification', fetchData);

 const {
  handleSubmit,
  formState: { isSubmitting },
 } = useForm();

 const questionnaireOptions = useMemo(() => {
  return (
   questionnaires?.map((questionnaire) => ({
    label: questionnaire.description,
    value: questionnaire.id_survey,
   })) || []
  );
 }, [questionnaires]);

 const handleQuestionnaireSelect = (value) => {
  setSelectedQuestionnaire(value);
 };

 const handleAddQuestionnaire = () => {
  const selectedData = questionnaires.find(
   (q) => q.id_survey === Number(selectedQuestionnaire)
  );
  setChosenQuestionnaire(selectedData);
  setSelectedQuestionnaire(null);

  if (selectedData.has_pillar) {
   const initialScores = selectedData?.pillars?.reduce((acc, pillar) => {
    acc[pillar?.id_pillar] = '';
    return acc;
   }, {});
   setCutoffScores(initialScores);
  } else {
   setCutoffScores({ main: '' });
  }
 };

 const handleRemoveQuestionnaire = () => {
  setChosenQuestionnaire(null);
  setSelectedQuestionnaire(null);
  setCutoffScores({});
 };

 const handleNameChange = (e) => {
  setCertificationName(e.target.value);
 };

 const handleExerciseChange = (date) => {
  setCertificationExercise(date);
 };

 const handleCutoffScoreChange = (value, pillarId = 'main') => {
  setCutoffScores((prev) => ({
   ...prev,
   [pillarId]: value,
  }));
 };

 useEffect(() => {
  if (certificateData && certificateData?.length > 0) {
   const certification = certificateData[0];
   setCertificationName(certification.name);
   setInitialCertificationName(certification.name);
   const fromMonth = dateToMonth(certification.valid_from);
   const untilMonth = dateToMonth(certification.valid_until);
   setValidFromMonth(fromMonth);
   setInitialValidFromMonth(fromMonth);
   setValidUntilMonth(untilMonth);
   setInitialValidUntilMonth(untilMonth);
   const fromMonth2 = dateToMonth(certification.valid_from_2);
   const untilMonth2 = dateToMonth(certification.valid_until_2);
   setValidFromMonth2(fromMonth2);
   setInitialValidFromMonth2(fromMonth2);
   setValidUntilMonth2(untilMonth2);
   setInitialValidUntilMonth2(untilMonth2);
   const model = certification.seal_model || 'ESG';
   setSealModel(model);
   setInitialSealModel(model);
   const model2 = certification.seal_model_2 || 'ESG';
   setSealModel2(model2);
   setInitialSealModel2(model2);
   const s2 = !!certification.seal_2_enabled;
   setSeal2Enabled(s2);
   setInitialSeal2Enabled(s2);
   const exerciseDate = certification.exercise ? new Date(certification.exercise, 0, 1) : null;
   setCertificationExercise(exerciseDate);
   setInitialCertificationExercise(exerciseDate);

   if (certification?.surveys && certification?.surveys?.length > 0) {
    const relatedSurvey = certification.surveys[0];
    // O questionário do próprio certificado NÃO costuma estar no dropdown
    // /survey/certification (que lista só os disponíveis p/ vincular). Logo,
    // pré-preenche a partir dos dados do PRÓPRIO certificado (findOne já traz
    // name/cutoff/pillars); o match no dropdown só enriquece (total_questions).
    const matchingQuestionnaire = questionnaires?.find(
     (q) => q?.id_survey === relatedSurvey?.id
    );

    const questionnaireWithScores = {
     ...(matchingQuestionnaire || {}),
     id_survey: relatedSurvey.id,
     description: relatedSurvey.name ?? matchingQuestionnaire?.description,
     type: relatedSurvey.type ?? matchingQuestionnaire?.type,
     index: relatedSurvey.index,
     id_link: relatedSurvey.id_link,
     has_pillar: relatedSurvey.has_pillar,
     pillars: relatedSurvey.pillars,
    };

    setChosenQuestionnaire(questionnaireWithScores);
    setInitialChosenQuestionnaire(questionnaireWithScores);

    // Set cutoff scores based on whether the questionnaire has pillars
    if (relatedSurvey.has_pillar && relatedSurvey.pillars) {
     const pillarScores = relatedSurvey.pillars.reduce((acc, pillar) => {
      acc[pillar.id_pillar] = pillar.cutoff_score?.toString() || '';
      return acc;
     }, {});
     setCutoffScores(pillarScores);
     setInitialCutoffScores(pillarScores);
    } else {
     const mainScore = relatedSurvey.cutoff_score?.toString() || '';
     setCutoffScores({ main: mainScore });
     setInitialCutoffScores({ main: mainScore });
    }
   }
   setIsQuestionnaireCheckComplete(true);
  }
 }, [certificateData, questionnaires]);

 useEffect(() => {
  const hasNameChange = certificationName !== initialCertificationName;
  const hasExerciseChange =
   (certificationExercise?.getFullYear() ?? null) !==
   (initialCertificationExercise?.getFullYear() ?? null);
  const hasQuestionnaireChange =
   chosenQuestionnaire?.id_survey !== initialChosenQuestionnaire?.id_survey;
  const hasScoreChanges = Object.entries(cutoffScores).some(
   ([key, value]) => value !== initialCutoffScores[key]
  );
  const hasValidityChange =
   validFromMonth !== initialValidFromMonth ||
   validUntilMonth !== initialValidUntilMonth ||
   validFromMonth2 !== initialValidFromMonth2 ||
   validUntilMonth2 !== initialValidUntilMonth2;
  const hasSealModelChange =
   sealModel !== initialSealModel ||
   sealModel2 !== initialSealModel2 ||
   seal2Enabled !== initialSeal2Enabled;

  const areCutoffScoresFilled = Object.values(cutoffScores).every(
   (score) => score !== '' && score !== null && score !== undefined
  );

  const areAllPillarsFilled = chosenQuestionnaire?.has_pillar
   ? chosenQuestionnaire.pillars?.every(
      (pillar) =>
       cutoffScores[pillar?.id_pillar] !== '' &&
       cutoffScores[pillar?.id_pillar] !== null &&
       cutoffScores[pillar?.id_pillar] !== undefined
     )
   : cutoffScores.main !== '' &&
     cutoffScores.main !== null &&
     cutoffScores.main !== undefined;

  // EG-27: exercício é OPCIONAL (igual no cadastro) — não pode travar o Salvar.
  // Certificações sem exercício (ex.: legadas) precisam poder salvar selo/validade.
  setIsSubmitEnabled(
   !!certificationName &&
    !!chosenQuestionnaire &&
    areCutoffScoresFilled &&
    areAllPillarsFilled &&
    (hasNameChange || hasExerciseChange || hasQuestionnaireChange || hasScoreChanges || hasValidityChange || hasSealModelChange)
  );
 }, [
  certificationName,
  certificationExercise,
  chosenQuestionnaire,
  initialCertificationName,
  initialCertificationExercise,
  initialChosenQuestionnaire,
  cutoffScores,
  initialCutoffScores,
  validFromMonth,
  initialValidFromMonth,
  validUntilMonth,
  initialValidUntilMonth,
  validFromMonth2,
  initialValidFromMonth2,
  validUntilMonth2,
  initialValidUntilMonth2,
  sealModel,
  initialSealModel,
  sealModel2,
  initialSealModel2,
  seal2Enabled,
  initialSeal2Enabled,
 ]);

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const closeSuccessModal = () => {
  setIsSubmitSuccessful(false);
  mutate();
  navigate('/certifications');
 };

 const onSubmit = async () => {
  try {
   const payload = {
    name: certificationName,
    seal_model: sealModel,
    seal_model_2: seal2Enabled ? sealModel2 : null,
    seal_2_enabled: seal2Enabled,
    valid_from: validFromMonth ? monthToStartDate(validFromMonth) : null,
    valid_until: validUntilMonth ? monthToEndDate(validUntilMonth) : null,
    valid_from_2:
     seal2Enabled && validFromMonth2 ? monthToStartDate(validFromMonth2) : null,
    valid_until_2:
     seal2Enabled && validUntilMonth2 ? monthToEndDate(validUntilMonth2) : null,
    exercise: certificationExercise ? certificationExercise.getFullYear() : undefined,
   };

   // Só mexe nos vínculos de questionário quando ele REALMENTE mudou — reenviar o
   // mesmo survey faz o backend recusar ("já vinculado a outra certificação").
   const questionnaireChanged =
    chosenQuestionnaire?.id_survey !== initialChosenQuestionnaire?.id_survey;
   if (questionnaireChanged) {
    payload.linkCertificates = [
     {
      id: chosenQuestionnaire.id_survey,
      id_link: chosenQuestionnaire.id_link,
      name: chosenQuestionnaire.description,
      type: chosenQuestionnaire.type,
      index: chosenQuestionnaire.index,
      ...(chosenQuestionnaire?.has_pillar
       ? {
          pillar_scores: chosenQuestionnaire.pillars.map((pillar) => ({
           id_pillar: pillar.id_pillar,
           score: Number(cutoffScores[pillar.id_pillar]),
          })),
         }
       : { cutoff_score: Number(cutoffScores.main) }),
     },
     {
      id: initialChosenQuestionnaire.id_survey,
      id_link: initialChosenQuestionnaire.id_link,
      name: initialChosenQuestionnaire.description,
      type: initialChosenQuestionnaire.type,
      index: initialChosenQuestionnaire.index,
      delete: true,
     },
    ];
   }

   await baseService.patch(`certificate/${certificationId}`, payload);
   setIsSubmitSuccessful(true);
  } catch (error) {
   if (error?.response?.status === 400 && error?.response?.data?.message) {
    showErrorToast(error.response.data.message);
   } else {
    showErrorToast('Não foi possível salvar a certificação');
   }
  }
 };

 if (isLoading || !isQuestionnaireCheckComplete) {
  return <Loader />;
 }

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <QuestionnaireHeaderNew
    heading='Editar Certificação'
    isSubmitting={isSubmitting}
    isSubmitEnabled={isSubmitEnabled}
    isSubmit={true}
    submitLabel='Salvar'
   >
    <InputText
     placeholder='Digite o nome da certificação'
     autoComplete='off'
     width='480px'
     maxW='90%'
     value={certificationName}
     onChange={handleNameChange}
    />
    <YearPickerInput
     value={certificationExercise}
     onChange={handleExerciseChange}
     placeholder='Exercício'
    />
   </QuestionnaireHeaderNew>

   <Center my={['8px', '24px']} px={{ xl: '86px', '2xl': 'unset' }}>
    <Container maxW={'1280px'} p={0} minH='80vh'>
     <Stack bg='white' spacing='24px' p='6' borderRadius='12px'>
      <Box>
       <Heading as='h3' fontSize='md' fontWeight={600}>
        Selos e guia de uso
       </Heading>
       <Text fontSize='sm' color='#5C6066' mt='4px'>
        Cada selo tem tipo, imagem e validade próprios. Sem upload, usa-se o
        modelo do tipo escolhido (ou o selo padrão do sistema). As alterações de
        tipo/validade salvam ao clicar em Salvar (topo).
       </Text>
      </Box>

      {/* Selo 1 */}
      <Box border='2px solid #EAEEF0' borderRadius='12px' p='20px'>
       <Heading as='h4' fontSize='15px' fontWeight={600} mb='16px'>
        Selo 1
       </Heading>
       <Box maxW='360px' mb='16px'>
        <Select
         label='Tipo do selo'
         value={sealModel}
         onChange={(e) => setSealModel(e.target.value)}
         options={SEAL_MODELS.map((m) => ({ label: m.label, value: m.value }))}
        />
       </Box>
       <SealAssetInput
        label='Imagem do selo 1'
        currentUrl={certificateAssets?.seal?.url}
        defaultUrl={modelUrl(sealModel) || defaultAssets?.seal?.url}
        onUpload={handleSealUpload}
        onReset={handleSealReset}
       />
       <MonthRangeFields
        label='Validade do selo 1 (mês/ano)'
        from={validFromMonth}
        until={validUntilMonth}
        onFrom={setValidFromMonth}
        onUntil={setValidUntilMonth}
       />
      </Box>

      {/* Selo 2 (opcional) */}
      {!seal2Enabled ? (
       <Button
        variant='outline'
        alignSelf='flex-start'
        h='48px'
        px='24px'
        borderWidth='2px'
        borderStyle='dashed'
        borderColor='#0073BB'
        color='#0073BB'
        fontWeight={700}
        _hover={{ bg: '#EAF3FB' }}
        onClick={() => setSeal2Enabled(true)}
       >
        + Adicionar segundo selo
       </Button>
      ) : (
       <Box border='2px solid #EAEEF0' borderRadius='12px' p='20px'>
        <Flex justify='space-between' align='center' mb='16px'>
         <Heading as='h4' fontSize='15px' fontWeight={600}>
          Selo 2
         </Heading>
         <Button
          variant='ghost'
          size='sm'
          color='#EA582B'
          onClick={() => setSeal2Enabled(false)}
         >
          Desabilitar
         </Button>
        </Flex>
        <Box maxW='360px' mb='16px'>
         <Select
          label='Tipo do selo'
          value={sealModel2}
          onChange={(e) => setSealModel2(e.target.value)}
          options={SEAL_MODELS.map((m) => ({ label: m.label, value: m.value }))}
         />
        </Box>
        <SealAssetInput
         label='Imagem do selo 2'
         currentUrl={certificateAssets?.seal2?.url}
         defaultUrl={modelUrl(sealModel2) || defaultAssets?.seal?.url}
         hint='Sem upload, usa-se a arte do modelo (ou o selo padrão do sistema). Lembre de salvar (topo) para habilitar.'
         onUpload={handleSeal2Upload}
         onReset={handleSeal2Reset}
        />
        <MonthRangeFields
         label='Validade do selo 2 (mês/ano)'
         from={validFromMonth2}
         until={validUntilMonth2}
         onFrom={setValidFromMonth2}
         onUntil={setValidUntilMonth2}
        />
       </Box>
      )}

      <SealAssetInput
       label='Guia de uso (PDF)'
       noun='guia'
       kind='file'
       accept='.pdf'
       currentFilename={certificateAssets?.guide?.filename}
       defaultFilename={defaultAssets?.guide?.filename}
       hint='PDF com instruções de uso do selo.'
       onUpload={handleGuideUpload}
       onReset={handleGuideReset}
      />
     </Stack>

     <HStack
      bg='white'
      mt='8px'
      justify='space-between'
      p='6'
      borderRadius='12px 12px 0 0'
      borderBottom='2px solid #F5F5FB'
     >
      <Heading as='h3' fontSize='md' fontWeight={600}>
       Questionários
      </Heading>
     </HStack>

     <Stack bg='white' spacing='0' p='6' borderRadius='0 0 12px 12px'>
      <HStack spacing={6} align='end'>
       <Select
        label='Questionário'
        placeholder='Selecione um questionário'
        options={questionnaireOptions}
        onChange={(e) => handleQuestionnaireSelect(e.target.value)}
        isDisabled={!!chosenQuestionnaire}
       />

       <Button
        variant='primary'
        size='medium'
        w='137px'
        minW='137px'
        onClick={handleAddQuestionnaire}
        isDisabled={!selectedQuestionnaire || !!chosenQuestionnaire}
       >
        Adicionar
       </Button>
      </HStack>
     </Stack>

     {chosenQuestionnaire && (
      <Stack bg='white' spacing='6' p='6' borderRadius='0 0 12px 12px'>
       {!chosenQuestionnaire?.has_pillar ? (
        <Flex
         p='6'
         border='2px solid #EAEEF0'
         align='center'
         justify='space-between'
         gap='6'
         borderRadius='12px'
        >
         <Box fontWeight='600'>
          <Text lineHeight='28px'>{chosenQuestionnaire?.description}</Text>
          {chosenQuestionnaire?.total_questions != null && (
           <Text as='span' fontSize='sm' color='#5C6066'>
            {chosenQuestionnaire?.total_questions} questões
           </Text>
          )}
         </Box>

         <Flex align='center' gap='4' ml='auto'>
          <Text fontSize='sm' color='#5C6066' fontWeight={600} whiteSpace='nowrap'>
           Nota de corte*
          </Text>
          <InputText
           placeholder='Digite a nota de corte para certificação'
           value={cutoffScores.main}
           onChange={(e) => handleCutoffScoreChange(e.target.value)}
           type='number'
           min='0'
           w='304px'
           autoComplete='off'
          />
         </Flex>
         <ButtonOnlyIcon name='trash' h='32px' onClick={handleRemoveQuestionnaire} />
        </Flex>
       ) : (
        <Stack spacing='6' width='100%'>
         <Flex align='center' justify='space-between' pr='32px'>
          <Text fontWeight='bold' lineHeight='28px'>
           {chosenQuestionnaire?.description}
          </Text>
          <ButtonOnlyIcon name='trash' h='32px' onClick={handleRemoveQuestionnaire} />
         </Flex>

         <Stack spacing='12px'>
          {chosenQuestionnaire?.pillars?.map((pillar) => (
           <Flex
            key={pillar?.id_pillar}
            h='96px'
            p='6'
            border='2px solid #EAEEF0'
            align='center'
            justify='space-between'
            gap='6'
            borderRadius='12px'
           >
            <Box fontWeight='600'>
             <Text lineHeight='30px'>{pillar?.name_pillar}</Text>
             <Text as='span' fontSize='sm' color='#5C6066'>
              {pillar?.total_questions} questões
             </Text>
            </Box>

            <Flex align='center' gap='4' ml='auto'>
             <Text fontSize='sm' color='#5C6066' fontWeight={600} whiteSpace='nowrap'>
              Nota de corte*
             </Text>
             <InputText
              placeholder='Digite a nota de corte para certificação'
              value={cutoffScores[pillar?.id_pillar]}
              onChange={(e) => handleCutoffScoreChange(e.target.value, pillar?.id_pillar)}
              type='number'
              min='0'
              w='304px'
              autoComplete='off'
             />
            </Flex>
           </Flex>
          ))}
         </Stack>
        </Stack>
       )}

       <Flex
        align='center'
        bg='rgba(234, 88, 43, 0.1)'
        h='43px'
        p='12px'
        borderRadius='8px'
       >
        <Text fontSize='sm' color='#EA582B'>
         *A <b>nota de corte</b> significa que a Empresa para estar apta para se
         certificar deverá ter uma nota <b>igual</b> ou <b>superior</b> ao valor definido
         {chosenQuestionnaire.has_pillar ? ' em cada um dos pilares' : ''}.
        </Text>
       </Flex>
      </Stack>
     )}

    </Container>
   </Center>
   <SuccessModal
    isOpen={isSubmitSuccessful}
    onClose={closeSuccessModal}
    title='Certificação editada com sucesso!'
   />
  </form>
 );
}
